import { getGlobalCache, getGlobalCacheWithMeta, setGlobalCache } from '../supabaseCache';
import { getGoogleMapsDistanceViaEdge } from '../googleMapsEdge';
import { getHereMapsDistance } from '../hereMaps';
import {
    ROUTING_GLOBAL_CACHE_MAX_AGE_MS,
    ROUTING_PROVIDER_HEALTH_CACHE_KEY,
    ROUTING_PROVIDER_HEALTH_HISTORY_LIMIT
} from '../logistics/constants';

/**
 * Normaliza coordenadas para 4 casas decimais para maximizar cache hit (~11m de precisão).
 */
export const getLegKey = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    return `leg_${lat1.toFixed(4)}_${lng1.toFixed(4)}_${lat2.toFixed(4)}_${lng2.toFixed(4)}`;
};

export interface DistanceResult {
    minutes: number;
    distanceKm: number;
    source: 'cache' | 'here' | 'google';
}

export type RoutingProviderId = 'here' | 'google' | 'osrm';
export type RoutingProviderOutcome = 'success' | 'fallback' | 'error';
export type RoutingProviderStatus = 'healthy' | 'degraded' | 'down' | 'unknown';

export interface RoutingProviderHealthEvent {
    provider: RoutingProviderId;
    outcome: RoutingProviderOutcome;
    checkedAt: number;
    latencyMs?: number;
    fallbackTo?: RoutingProviderId;
    errorMessage?: string;
}

export interface RoutingProviderHealthState {
    provider: RoutingProviderId;
    status: RoutingProviderStatus;
    lastCheckAt: number | null;
    lastSuccessAt: number | null;
    lastErrorAt: number | null;
    lastLatencyMs: number | null;
    consecutiveErrors: number;
    successCount: number;
    fallbackCount: number;
    errorCount: number;
    lastErrorMessage: string | null;
    history: RoutingProviderHealthEvent[];
}

export interface RoutingProviderHealthSnapshot {
    updatedAt: number;
    providers: Record<RoutingProviderId, RoutingProviderHealthState>;
}

export interface RoutingProviderHealthUpdate {
    provider: RoutingProviderId;
    outcome: RoutingProviderOutcome;
    latencyMs?: number;
    fallbackTo?: RoutingProviderId;
    error?: string;
    checkedAt?: number;
}

const ROUTING_PROVIDERS: RoutingProviderId[] = ['here', 'google', 'osrm'];

const createEmptyProviderHealth = (provider: RoutingProviderId): RoutingProviderHealthState => ({
    provider,
    status: 'unknown',
    lastCheckAt: null,
    lastSuccessAt: null,
    lastErrorAt: null,
    lastLatencyMs: null,
    consecutiveErrors: 0,
    successCount: 0,
    fallbackCount: 0,
    errorCount: 0,
    lastErrorMessage: null,
    history: []
});

const createDefaultHealthSnapshot = (): RoutingProviderHealthSnapshot => ({
    updatedAt: Date.now(),
    providers: {
        here: createEmptyProviderHealth('here'),
        google: createEmptyProviderHealth('google'),
        osrm: createEmptyProviderHealth('osrm')
    }
});

const trimHealthHistory = (history: RoutingProviderHealthEvent[]): RoutingProviderHealthEvent[] => {
    if (history.length <= ROUTING_PROVIDER_HEALTH_HISTORY_LIMIT) return history;
    return history.slice(history.length - ROUTING_PROVIDER_HEALTH_HISTORY_LIMIT);
};

const sanitizeErrorMessage = (error?: string): string | null => {
    if (!error) return null;
    const normalized = error.trim();
    return normalized.length > 0 ? normalized.slice(0, 240) : null;
};

const resolveProviderStatus = (outcome: RoutingProviderOutcome, consecutiveErrors: number): RoutingProviderStatus => {
    if (outcome === 'success') return 'healthy';
    if (outcome === 'error' && consecutiveErrors >= 3) return 'down';
    if (outcome === 'error' || outcome === 'fallback') return 'degraded';
    return 'unknown';
};

const applyHealthUpdate = (
    snapshot: RoutingProviderHealthSnapshot,
    update: RoutingProviderHealthUpdate
): RoutingProviderHealthSnapshot => {
    const checkedAt = update.checkedAt ?? Date.now();
    const previous = snapshot.providers[update.provider] ?? createEmptyProviderHealth(update.provider);
    const errorMessage = sanitizeErrorMessage(update.error);
    const nextConsecutiveErrors = update.outcome === 'success' ? 0 : previous.consecutiveErrors + 1;

    const nextEvent: RoutingProviderHealthEvent = {
        provider: update.provider,
        outcome: update.outcome,
        checkedAt,
        latencyMs: Number.isFinite(update.latencyMs) ? update.latencyMs : undefined,
        fallbackTo: update.fallbackTo,
        errorMessage: errorMessage ?? undefined
    };

    const nextState: RoutingProviderHealthState = {
        ...previous,
        status: resolveProviderStatus(update.outcome, nextConsecutiveErrors),
        lastCheckAt: checkedAt,
        lastSuccessAt: update.outcome === 'success' ? checkedAt : previous.lastSuccessAt,
        lastErrorAt: update.outcome !== 'success' ? checkedAt : previous.lastErrorAt,
        lastLatencyMs: Number.isFinite(update.latencyMs) ? (update.latencyMs as number) : previous.lastLatencyMs,
        consecutiveErrors: nextConsecutiveErrors,
        successCount: previous.successCount + (update.outcome === 'success' ? 1 : 0),
        fallbackCount: previous.fallbackCount + (update.outcome === 'fallback' ? 1 : 0),
        errorCount: previous.errorCount + (update.outcome === 'error' ? 1 : 0),
        lastErrorMessage: update.outcome === 'success' ? null : (errorMessage ?? previous.lastErrorMessage),
        history: trimHealthHistory([...previous.history, nextEvent])
    };

    return {
        ...snapshot,
        updatedAt: checkedAt,
        providers: {
            ...snapshot.providers,
            [update.provider]: nextState
        }
    };
};

const normalizeSnapshot = (snapshot: unknown): RoutingProviderHealthSnapshot => {
    if (!snapshot || typeof snapshot !== 'object') {
        return createDefaultHealthSnapshot();
    }

    const safeSnapshot = snapshot as Partial<RoutingProviderHealthSnapshot>;
    const base = createDefaultHealthSnapshot();

    for (const provider of ROUTING_PROVIDERS) {
        const incoming = (safeSnapshot.providers as Record<string, Partial<RoutingProviderHealthState>> | undefined)?.[provider];
        if (!incoming) continue;

        base.providers[provider] = {
            ...base.providers[provider],
            ...incoming,
            provider,
            history: Array.isArray(incoming.history)
                ? trimHealthHistory(incoming.history.filter((entry): entry is RoutingProviderHealthEvent => !!entry && typeof entry === 'object'))
                : []
        };
    }

    base.updatedAt = Number.isFinite(safeSnapshot.updatedAt) ? (safeSnapshot.updatedAt as number) : base.updatedAt;
    return base;
};

const extractValueUpdatedAt = (value: unknown): number | null => {
    if (!value || typeof value !== 'object') return null;

    const candidate = (value as { updatedAt?: unknown }).updatedAt;
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
    if (typeof candidate === 'string') {
        const parsed = Date.parse(candidate);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const isCacheEntryFresh = (updatedAt: number | null): boolean => {
    if (!Number.isFinite(updatedAt)) return false;
    const ageMs = Date.now() - (updatedAt as number);
    return ageMs >= 0 && ageMs <= ROUTING_GLOBAL_CACHE_MAX_AGE_MS;
};

export const getRoutingProviderHealthSnapshot = async (): Promise<RoutingProviderHealthSnapshot> => {
    const stored = await getGlobalCache<RoutingProviderHealthSnapshot>(ROUTING_PROVIDER_HEALTH_CACHE_KEY);
    return normalizeSnapshot(stored);
};

export const recordRoutingProviderHealth = async (update: RoutingProviderHealthUpdate): Promise<void> => {
    try {
        const current = await getRoutingProviderHealthSnapshot();
        const next = applyHealthUpdate(current, update);
        await setGlobalCache(ROUTING_PROVIDER_HEALTH_CACHE_KEY, 'routing_provider_health', next);
    } catch (error) {
        console.error('[RoutingHealth] Failed to persist provider health:', error);
    }
};

const trackRoutingProviderHealth = (update: RoutingProviderHealthUpdate): void => {
    void recordRoutingProviderHealth(update);
};

/**
 * v4.1: Centralized Distance Matrix with Global Caching + HERE Primary + Google Fallback
 */
export const getDistanceMatrix = async (
    originLat: number, originLng: number,
    destLat: number, destLng: number,
    apiKey?: string,        // Google Maps API Key (fallback)
    hereApiKey?: string     // HERE API Key (primary)
): Promise<DistanceResult | null> => {

    const legKey = getLegKey(originLat, originLng, destLat, destLng);

    // 1. TENTA CACHE GLOBAL (matrix_cache)
    try {
        const cached = await getGlobalCacheWithMeta<{ minutes: number; distanceKm: number; updatedAt?: number | string }>(legKey);
        const cachedValue = cached.value;
        const cachedUpdatedAt = extractValueUpdatedAt(cachedValue) ?? cached.updatedAt;

        if (cachedValue && isCacheEntryFresh(cachedUpdatedAt)) {
            return {
                minutes: cachedValue.minutes,
                distanceKm: cachedValue.distanceKm,
                source: 'cache'
            };
        }

        if (cachedValue && !isCacheEntryFresh(cachedUpdatedAt)) {
            // Cache stale, will recalculate
        }
    } catch (e) {
        console.error("[DistanceCache] Read Error:", e);
    }

    // 2. HERE MAPS API (via Edge Function - PRIMARY)
    if (hereApiKey) {
        try {
            const startedAt = Date.now();

            const hereData = await getHereMapsDistance(
                originLat, originLng,
                destLat, destLng,
                hereApiKey
            );
            const latencyMs = Date.now() - startedAt;

            if (hereData && hereData.distanceKm > 0) {
                const result: DistanceResult = {
                    minutes: hereData.durationMins,
                    distanceKm: hereData.distanceKm,
                    source: 'here'
                };

                // SALVA NO CACHE GLOBAL
                setGlobalCache(legKey, 'leg_road', {
                    ...result,
                    updatedAt: Date.now(),
                    origin: { lat: originLat, lng: originLng },
                    dest: { lat: destLat, lng: destLng }
                }).catch(err => console.error("[DistanceCache] Write Error:", err));

                trackRoutingProviderHealth({
                    provider: 'here',
                    outcome: 'success',
                    latencyMs
                });

                return result;
            } else {
                console.warn('[DistanceAPI] HERE Maps retornou dados vazios, tentando Google...');
                trackRoutingProviderHealth({
                    provider: 'here',
                    outcome: apiKey ? 'fallback' : 'error',
                    fallbackTo: apiKey ? 'google' : undefined,
                    latencyMs,
                    error: 'HERE returned empty payload'
                });
            }
        } catch (e: any) {
            console.error("[DistanceAPI] HERE Maps Error:", e.message);
            trackRoutingProviderHealth({
                provider: 'here',
                outcome: apiKey ? 'fallback' : 'error',
                fallbackTo: apiKey ? 'google' : undefined,
                error: e?.message ?? 'HERE request failed'
            });
        }
    }

    // 3. GOOGLE MAPS API (via Edge Function para evitar CORS - FALLBACK)
    if (!apiKey) {
        console.warn('[DistanceAPI] API Key não informada (nem HERE nem Google)');
        return null;
    }

    try {
        // Usar Edge Function para evitar CORS (chamada do navegador)
        const startedAt = Date.now();

        const googleData = await getGoogleMapsDistanceViaEdge(
            originLat, originLng,
            destLat, destLng,
            apiKey
        );
        const latencyMs = Date.now() - startedAt;

        if (googleData && googleData.distanceKm > 0) {
            const result: DistanceResult = {
                minutes: googleData.durationMins,
                distanceKm: googleData.distanceKm,
                source: 'google'
            };

            // SALVA NO CACHE GLOBAL
                setGlobalCache(legKey, 'leg_road', {
                    ...result,
                    updatedAt: Date.now(),
                    origin: { lat: originLat, lng: originLng },
                    dest: { lat: destLat, lng: destLng }
                }).catch(err => console.error("[DistanceCache] Write Error:", err));

                trackRoutingProviderHealth({
                    provider: 'google',
                    outcome: hereApiKey ? 'fallback' : 'success',
                    latencyMs
                });

                return result;
        } else {
            console.warn('[DistanceAPI] Google Maps retornou dados vazios');
            trackRoutingProviderHealth({
                provider: 'google',
                outcome: 'error',
                latencyMs,
                error: 'Google returned empty payload'
            });
            return null;
        }
    } catch (e: any) {
        console.error("[DistanceAPI] Google Maps Error:", e.message);
        trackRoutingProviderHealth({
            provider: 'google',
            outcome: 'error',
            error: e?.message ?? 'Google request failed'
        });
        return null;
    }
};
